/**
 * Shared CSS-variable NAME helpers for the managed table layout.
 *
 * The names are the wire format between the JS that writes geometry (`useDataTableLayout`) and
 * the CSS that reads it (`dataTable.module.css`), so they live in one place. A nested table that
 * has to line its columns up with the parent list's resolved widths reads them from here too
 * (My Portfolio's tier-2 view).
 *
 * Only the var-NAME helpers (and the private `sanitizeId`) live here. The runtime machinery
 * that WRITES these variables — `writeVars`, the rAF / ResizeObserver / data-scrolled logic —
 * stays in `useDataTableLayout`.
 */

/** Keep alphanumerics, `_` and `-`; map the rest to `_` so a column id with selector-special
 *  chars still yields a valid custom-property name. */
const sanitizeId = (id: string): string => id.replace(/[^a-zA-Z0-9_-]/g, '_');

/** CSS variable holding a managed column's resolved width, scoped on the table container. */
export const columnWidthVar = (id: string): string => `--utw-w-${sanitizeId(id)}`;

/** CSS variable holding a sticky column's cumulative left offset. */
export const stickyLeftVar = (id: string): string => `--utw-left-${sanitizeId(id)}`;

/** CSS variable holding the sum of all column widths (the table's min-width). */
export const TABLE_MIN_WIDTH_VAR = '--utw-table-min';

// Column drag-reorder: a sortable header cell broadcasts its live dnd-kit state to these
// table-scoped CSS variables; the matching body cells read them so the column moves / dims in
// lockstep with its header — with no body re-render per frame (same CSS-variable pattern resize
// uses). Each defaults to a no-op so idle body cells render byte-identically.
// The opaque, lifted, grey-outlined preview of the dragged column lives in a dnd-kit
// `DragOverlay` (ColumnDragPreview); the original cells stay in place and merely dim + shift to
// their sorted slot — so this is intentionally just transform / transition / opacity / a grey
// drop-target line, not the full opaque-cover machinery the no-overlay version needed.

/** Live `transform` for a column's body cells (strategy shift while sorting, or `none`). */
export const columnDragXVar = (id: string): string => `--utw-dx-${sanitizeId(id)}`;

/** `transition` for a column's body cells so they ease to their sorted slot (or `none`). */
export const columnDragTransitionVar = (id: string): string => `--utw-dtr-${sanitizeId(id)}`;

/** Opacity for a column's body cells — `0.4` dims the dragged column into a placeholder while
 *  its opaque clone floats in the overlay (else `1`). */
export const columnDragOpacityVar = (id: string): string => `--utw-dop-${sanitizeId(id)}`;

/** `box-shadow` for a column's body cells — a thin grey insertion line on the single edge
 *  where the dragged column will arrive, set only on the column the pointer is over (else `none`). */
export const columnDropEdgeVar = (id: string): string => `--utw-dedge-${sanitizeId(id)}`;
